import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { get as _get, some as _some } from 'lodash';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

// Store
import { claimFnolQuery, DraftClaim, fromClaimFnolActions } from '@amfam/claim/fnol/data-access';
import { PolicySelectors } from '@amfam/policy/data-access';
import { FnolRiskModel } from '@amfam/policy/models';
import { ClaimsNavigationPaths, ResumeDraftClaimModel } from '@amfam/shared/models';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access';
import { fromRouterActions } from '@amfam/shared/utility/navigation';
import { LoadingSpinnerService, PageTitleService, SidebarService } from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';

@Component({
  selector: 'ds-report-claim-duplicate',
  templateUrl: './report-claim-duplicate.component.html',
  styleUrls: ['./report-claim-duplicate.component.scss'],
  standalone: false
})
export class ReportClaimDuplicateComponent implements OnInit, OnDestroy, AfterViewInit {
  // TODO: Create model
  public draftClaimsList = [];
  private draftClaimDetails = [];
  private claimInProgress: DraftClaim;
  private vinInProgress: string;
  private dateInProgress: string;
  public risks: Array<FnolRiskModel> = new Array<FnolRiskModel>();
  public sidebarPresent = false;

  private claimId: string;

  // Aggregate Loading Observable
  public loading: Observable<Boolean>;

  // Shared Subject for completing all observables on destroy
  private stop$: Subject<void> = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    public spinner: LoadingSpinnerService,
    private pageTitle: PageTitleService,
    private feature: FeatureFlagService,
    private store: Store<fromRoot.RootState>,
    private sidebarService: SidebarService
  ) {}

  ngOnInit() {
    this.spinner.start({
      blockActions: true
    });

    // Subscribe to get the route params
    this.route.params.pipe(takeUntil(this.stop$)).subscribe(params => {
      const id = params['id'];
      this.claimId = id;
    });

    this.pageTitle.set('Claims');

    // Aggregate loading spinner
    this.loading = this.store.select(claimFnolQuery.getSelectedEntityLoading);

    combineLatest(
      this.store.select(claimFnolQuery.getClaimFnols),
      this.store.select(claimFnolQuery.getClaimFnolSelectedEntity),
      this.store.select(PolicySelectors.selectClaimableRiskModels),
      this.loading,
      (fnols, selectedClaim, risks, loading) => {
        return {
          fnols: fnols,
          selectedClaim: selectedClaim,
          risks: risks,
          loading: loading
        };
      }
    )
      .pipe(
        filter(state => state && !_some(state.fnols, { loading: true })),
        takeUntil(this.stop$)
      )
      .subscribe(state => {
        this.spinner.stop();
        /**
         * AS: If the user has clicked on start a fresh claim, we wait for the fnol - POST response to decide where
         * we need to redirect the user. This conditions checks wheather we are just loading this page or if we have
         * clicked the button and waiting for the response.
         */
        if (this.claimInProgress) {
          if (_get(state, 'selectedClaim.claimNumber', '')) {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [
                  ClaimsNavigationPaths.CLAIM_HOME_PATH +
                    '/report-claim-fnol/' +
                    state.selectedClaim.claimNumber
                ]
              })
            );
          } else {
            this.store.dispatch(
              fromRouterActions.Go({
                path: [ClaimsNavigationPaths.CLAIM_INCIDENT_LOCATION_PATH]
              })
            );
          }
        }

        this.risks = _get(state, 'risks', []);

        /**
         * AS: Need to show the current active fnols which has the same vehicle and the date of loss.
         * For this we use the lossDate and the vin number of a matched draft claim. ( This matching was done in report claim incident time)
         * 1) Extract the date and Vin
         * 2) Filter the draft list to show only the matched ones.
         */
        this.dateInProgress = _get(state.selectedClaim, 'lossDate', '').toString();
        this.vinInProgress = _get(
          state.selectedClaim,
          'autoClaimDetail.vehicles[0].vin',
          ''
        ).toString();

        this.draftClaimsList = state.fnols.filter(
          fnolClaim =>
            fnolClaim.claimNumber &&
            String(_get(fnolClaim, 'lossDate', 'T')).split('T')[0] ===
              String(_get(state.selectedClaim, 'lossDate', 'T')).split('T')[0] &&
            _get(fnolClaim, 'autoClaimDetail.vehicles[0].vin') === this.vinInProgress
        );
        this.claimInProgress = state.selectedClaim;
      });
  }

  ngAfterViewInit() {
    window.setTimeout(() => {
      this.sidebarService.sidebarPresent$
        .pipe(takeUntil(this.stop$))
        .subscribe(present => (this.sidebarPresent = present));
    }, 0);
  }

  public startANewClaim(): void {
    this.store.dispatch(new fromClaimFnolActions.CreateDraftClaim(this.claimInProgress));
    this.spinner.start({
      blockActions: true
    });
  }

  public resumeDraftClaim(resumePayload: ResumeDraftClaimModel) {
    this.store.dispatch(
      fromRouterActions.Go({
        path: [ClaimsNavigationPaths.CLAIM_REPORTFNOL_HOME_PATH + '/' + resumePayload.claimNumber]
      })
    );
  }

  ngOnDestroy() {
    this.stop$.next();
    this.stop$.complete();
  }
}
